/**
 * Event Card Component - ANCHOR
 * Timesact 캘린더 위젯에서 이벤트 데이터를 가져와 카드로 표시
 *
 * 사용법:
 * 1. Liquid에서: {% render 'event-card', count: 8, size: 'small' %}
 * 2. JS에서 수동 초기화: EventCards.init(containerElement)
 * 3. 데이터만 가져오기: EventCards.getEvents()
 */

window.EventCards = (function() {
  'use strict';

  // 캐시된 이벤트 데이터
  let cachedEvents = null;
  let observerActive = false;

  /**
   * Timesact 캘린더 위젯(.tscListCard)에서 이벤트 데이터 추출
   */
  function extractEventsFromCalendar() {
    const calendarCards = document.querySelectorAll('.tscListCard');
    const events = [];

    calendarCards.forEach((card, index) => {
      // data 속성에서 정보 추출
      let productImage = card.dataset.productImage || '';
      let productTitle = card.dataset.productTitle || '';
      let productVendor = card.dataset.productVendor || '';
      let productUrl = card.dataset.productUrl || '#';

      // 이미지가 없으면 카드 내부 img에서 추출
      if (!productImage) {
        const imgEl = card.querySelector('img');
        if (imgEl) productImage = imgEl.src || '';
      }

      // 날짜 정보 추출 (h4 태그들에서)
      const headings = card.querySelectorAll('h4');
      let startDate = '', endDate = '';
      let releaseDate = null;

      if (headings.length >= 2) {
        startDate = headings[0].textContent.trim().replace(/\s+/g, ' ');
        endDate = headings[1].textContent.trim().replace(/\s+/g, ' ');
      }

      // 날짜 파싱 (종료 날짜 기준)
      const dateMatch = endDate.match(/([A-Za-z]+)\s*(\d+)/);
      if (dateMatch) {
        const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const monthIndex = monthNames.indexOf(dateMatch[1]);
        const day = parseInt(dateMatch[2]);
        const year = new Date().getFullYear();

        releaseDate = new Date(year, monthIndex, day);
        if (releaseDate < new Date()) {
          releaseDate.setFullYear(year + 1);
        }
      }

      // 이벤트 이름 추출
      let eventName = '';
      let timeInfo = '';
      const textNodes = card.querySelectorAll('div');
      textNodes.forEach(node => {
        const text = node.textContent.trim();
        if ((text.includes('test') || text.includes('event') || text.includes('Event')) && !text.includes('View Event')) {
          const lines = text.split('\n');
          if (lines[0] && !lines[0].includes('View')) {
            eventName = lines[0].trim();
          }
        }
        const timeMatch = text.match(/\d+:\d+\s*(AM|PM)\s*-\s*\d+:\d+\s*(AM|PM)/i);
        if (timeMatch) timeInfo = timeMatch[0];
      });

      if (!productTitle) productTitle = eventName || 'Upcoming Event';

      const dateStr = startDate && endDate ? startDate + ' \u2192 ' + endDate : '';

      events.push({
        index: index,
        image: productImage,
        title: productTitle,
        vendor: productVendor,
        url: productUrl,
        releaseDate: releaseDate || new Date(Date.now() + (index + 1) * 7 * 24 * 60 * 60 * 1000),
        dateStr: dateStr,
        timeStr: timeInfo,
        eventName: eventName
      });
    });

    return events;
  }

  /**
   * 이벤트 카드 HTML 생성
   */
  function renderCard(event, size) {
    const displayVendor = event.vendor || event.eventName || '';
    const displayTitle = event.title || 'Upcoming Event';
    const maxLength = size === 'small' ? 25 : (size === 'large' ? 50 : 40);
    const truncatedTitle = displayTitle.length > maxLength ? displayTitle.substring(0, maxLength) + '...' : displayTitle;

    let dateDisplay = event.dateStr || '';
    if (event.timeStr) {
      dateDisplay += (dateDisplay ? ' @ ' : '') + event.timeStr;
    }

    return `
      <a href="${event.url}" class="event-card"
         data-index="${event.index}"
         data-release-time="${event.releaseDate.getTime()}">
        <div class="event-card__image">
          ${event.image
            ? '<img src="' + event.image + '" alt="' + displayTitle + '" loading="lazy">'
            : '<div class="event-card__placeholder"></div>'
          }
        </div>
        <div class="event-card__info">
          <p class="event-card__vendor">${displayVendor}</p>
          <h4 class="event-card__title">${truncatedTitle}</h4>
          <p class="event-card__date">${dateDisplay}</p>
        </div>
      </a>
    `;
  }

  /**
   * 컨테이너에 카드 렌더링
   */
  function renderCards(container, events) {
    const grid = container.querySelector('.event-cards__grid');
    if (!grid) return;

    const count = parseInt(container.dataset.count) || 6;
    const size = container.dataset.size || 'medium';
    const displayEvents = events.slice(0, count);

    if (displayEvents.length === 0) {
      grid.innerHTML = '<div class="event-cards__empty">No upcoming events</div>';
      return;
    }

    grid.innerHTML = displayEvents.map(event => renderCard(event, size)).join('');

    // 카운트다운 설정 (있는 경우)
    const countdown = container.querySelector('[data-countdown]');
    if (countdown && displayEvents.length > 0) {
      setupCountdown(container, displayEvents[0].releaseDate);
    }

    // 호버 이벤트 설정
    setupHoverEvents(container);
  }

  /**
   * 호버 이벤트 설정
   */
  function setupHoverEvents(container) {
    const cards = container.querySelectorAll('.event-card');
    const countdown = container.querySelector('[data-countdown]');

    cards.forEach(card => {
      card.addEventListener('mouseenter', function() {
        cards.forEach(c => c.classList.remove('active'));
        this.classList.add('active');

        if (countdown) {
          const releaseTime = parseInt(this.dataset.releaseTime);
          updateCountdown(container, new Date(releaseTime));
        }
      });
    });

    // 첫 번째 카드 활성화
    if (cards.length > 0) {
      cards[0].classList.add('active');
    }
  }

  /**
   * 카운트다운 설정
   */
  let countdownIntervals = new Map();

  function setupCountdown(container, targetDate) {
    updateCountdown(container, targetDate);
  }

  function updateCountdown(container, targetDate) {
    const countdown = container.querySelector('[data-countdown]');
    if (!countdown) return;

    // 기존 인터벌 제거
    const existingInterval = countdownIntervals.get(container);
    if (existingInterval) clearInterval(existingInterval);

    function tick() {
      const now = new Date();
      const diff = targetDate - now;

      const daysEl = countdown.querySelector('[data-days]');
      const hoursEl = countdown.querySelector('[data-hours]');
      const minsEl = countdown.querySelector('[data-mins]');
      const secsEl = countdown.querySelector('[data-secs]');

      if (diff <= 0) {
        if (daysEl) daysEl.textContent = '00';
        if (hoursEl) hoursEl.textContent = '00';
        if (minsEl) minsEl.textContent = '00';
        if (secsEl) secsEl.textContent = '00';
        return;
      }

      const d = Math.floor(diff / (1000 * 60 * 60 * 24));
      const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((diff % (1000 * 60)) / 1000);

      if (daysEl) daysEl.textContent = String(d).padStart(2, '0');
      if (hoursEl) hoursEl.textContent = String(h).padStart(2, '0');
      if (minsEl) minsEl.textContent = String(m).padStart(2, '0');
      if (secsEl) secsEl.textContent = String(s).padStart(2, '0');
    }

    tick();
    const interval = setInterval(tick, 1000);
    countdownIntervals.set(container, interval);
  }

  /**
   * 단일 컨테이너 초기화
   */
  function initContainer(container) {
    if (container.dataset.initialized === 'true') return;

    const events = getEvents();
    if (events.length > 0) {
      renderCards(container, events);
      container.dataset.initialized = 'true';
    }
  }

  /**
   * 모든 이벤트 카드 컨테이너 초기화
   */
  function initAll() {
    const containers = document.querySelectorAll('[data-event-cards]');
    const events = getEvents();

    if (events.length > 0) {
      containers.forEach(container => {
        if (container.dataset.initialized !== 'true') {
          renderCards(container, events);
          container.dataset.initialized = 'true';
        }
      });
    } else if (!observerActive) {
      // Timesact 위젯이 로드되면 다시 시도
      observerActive = true;
      const observer = new MutationObserver((mutations, obs) => {
        const cards = document.querySelectorAll('.tscListCard');
        if (cards.length > 0) {
          cachedEvents = extractEventsFromCalendar();
          containers.forEach(container => {
            if (container.dataset.initialized !== 'true') {
              renderCards(container, cachedEvents);
              container.dataset.initialized = 'true';
            }
          });
          obs.disconnect();
          observerActive = false;
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      // 5초 후 타임아웃
      setTimeout(() => {
        if (observerActive) {
          observer.disconnect();
          observerActive = false;
        }
      }, 5000);
    }
  }

  /**
   * 이벤트 데이터 가져오기 (캐시됨)
   */
  function getEvents() {
    if (cachedEvents === null) {
      cachedEvents = extractEventsFromCalendar();
    }
    return cachedEvents;
  }

  /**
   * 캐시 초기화 (데이터 새로고침용)
   */
  function refreshEvents() {
    cachedEvents = extractEventsFromCalendar();
    return cachedEvents;
  }

  // DOM Ready 시 자동 초기화
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    // 약간의 딜레이를 두고 초기화 (Timesact 로딩 대기)
    setTimeout(initAll, 500);
  }

  // Public API
  return {
    init: initContainer,
    initAll: initAll,
    getEvents: getEvents,
    refreshEvents: refreshEvents,
    renderCard: renderCard
  };
})();
